import { Entity, EntityProfileType } from "@common/types/entity";
import { LineItemFallbackAccountType } from "@common/helpers/accounting";
import { InvoiceSource } from "@common/types/invoice";
import { ClassAssignment, PairedTransfer } from "@common/types/transaction";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { Taxonomy } from "@common/categories";
import { TransactionMatchSchema } from "@common/schemas/transactionMatch.schema";
import type { AccrualMatchSuggestionDisplay } from "@common/schemas/domains/accrualReview/accrual-match-suggestion-display.schema";
import type { TransactionPreviewData } from "@components/transactions/TransactionPreview/TransactionPreview";

export interface MatchableTransaction {
    id: number;
    amount: number;
    date: Date;
    dateOnly?: string | null;
    bankDescription: string;
    displayDescription: string | null;
    accrualAccountId?: string | null;
    categoryId?: string | null;
    isBusiness: boolean;
    deductionRate?: number | null;
    counterparty?: { name: string } | null;
    classAssignments?: ClassAssignment[];
    transactionMatches?: TransactionMatchSchema[];
    accrualMatchSuggestions?: AccrualMatchSuggestionDisplay[];
    pairedTransferId?: number | null;
    pairedTransfer?: PairedTransfer | null;
    category?: { taxonomy?: Taxonomy | null } | null;
    entity: Pick<
        Entity,
        "id" | "name" | "iconColor" | "workspaceId" | "logo"
    > & {
        legalType: EntityProfileType | null;
    };
    financialAccount?: { id?: number; entityId: number | null } | null;
    journalEntries?: Array<{
        accountingLedger: {
            basis: AccountingLedgerBasis;
            entityId: number;
        };
        lineOrder: number;
        accountId: string;
    }> | null;
    accountId?: string | null;
}

export enum MatchItemType {
    TRANSACTION = "Transaction",
    INVOICE = "Invoice",
    BILL = "Bill",
    LINE_ITEM = "Line item",
    REMAINING = "Remaining",
}

export interface MatchableItem {
    id: string;
    date: Date;
    dateOnly?: string | null;
    type: MatchItemType;
    counterpartyName: string | null;
    description: string | null;
    documentNumber?: string | null;
    bankDescription?: string | null;
    accountIds: string[];
    /**
     * The item's cash-ledger account(s), used by the dedicated "Cash ledger"
     * column in the default (workspace-matched) match drawer view so it can
     * render alongside "Accrual ledger" for dual-ledger workspaces without
     * the two basis columns fighting over the legacy single `accountIds`
     * field. For invoices this is the unique income accounts on the lines.
     * For bills this is the unique expense accounts on the lines. Optional
     * because it's only populated by the mappers that feed the main match
     * drawer (transactions, invoices, bills) and by resolve-difference rows,
     * which copy the source transaction's cash account so it can be edited.
     */
    cashAccountIds?: string[];
    /**
     * Line purposes that have no cash-side account coded. The cash ledger
     * cell fills each one with that purpose's default account. Invoices use
     * income, tax, and discount fallbacks; bills use the default expense
     * account.
     */
    missingIncomeFallbacks?: LineItemFallbackAccountType[];
    /**
     * The item's accrual-ledger account(s) (or a document's aggregated AR/AP
     * account(s)), used by the dedicated "Accrual ledger" column. See
     * `cashAccountIds` for why this is separate from `accountIds`.
     */
    accrualAccountIds?: string[];
    /**
     * An invoice line has no receivables account, so the accrual column
     * should include the default receivables account.
     */
    usesDefaultReceivableAccount?: boolean;
    /**
     * Cash-basis category of the transaction, shown in the "Category" column
     * for non-GL-first workspaces (which classify by category rather than GL
     * account). Null for invoice/bill items.
     */
    categoryId?: string | null;
    pairedTransferId?: number | null;
    /**
     * Preview of the transaction this item is already paired to (its own
     * transfer match), shown in the "Category" column's match link tooltip
     * so the user can see what a candidate row is matched to without
     * leaving the drawer. Distinct from the source transaction being
     * matched in this drawer.
     */
    matchPreview?: TransactionPreviewData | null;
    classIds: string[];
    amount: number;
    openAmount: number | null;
    entityId: number;
    /**
     * Entity whose ledger owns this item's GL accounts (`cashAccountIds` /
     * `accrualAccountIds`), used by the "Cash ledger" / "Accrual ledger"
     * columns to fetch the right chart of accounts. Differs from `entityId`
     * for a non-business transaction sitting in a business financial account
     * (its journal entries live in the business ledger, not the personal
     * entity's — see `transactionToMatchableItem`'s `entityIdForGLAccount`).
     * Undefined for items whose GL accounts belong to their own `entityId`
     * (invoices/bills, reverse-drawer snapshots); the ledger cells fall back
     * to `entityId` then.
     */
    ledgerEntityId?: number;
    entity: Pick<Entity, "id" | "name" | "iconColor" | "workspaceId" | "logo">;
    financialAccountId?: number | null;
    invoiceSource?: InvoiceSource | null;
    lineItems?: MatchableLineItem[];
    children?: MatchableItem[];
}

export interface MatchableLineItem {
    id: string;
    description: string | null;
    accountId: string | null;
    /**
     * Expense accounts on a bill line, shown in the allocation row's
     * "Expense account" column. Invoice lines leave this unset; their income
     * accounts stay on the parent document.
     */
    cashAccountIds?: string[];
    missingIncomeFallbacks?: LineItemFallbackAccountType[];
    classIds: string[];
    amount: number;
    openAmount: number;
}

export enum AccrualMatchColumn {
    /**
     * Read-only period-lock indicator (PRO-12200): shows the same lock icon
     * used on the Invoices/Bills tables when the item's date falls inside a
     * locked accounting period. Only offered on the default (workspace-
     * matched) match drawer view.
     */
    LOCK = "lock",
    DATE = "date",
    TYPE = "type",
    COUNTERPARTY = "counterparty",
    DESCRIPTION = "description",
    ACCOUNT = "account",
    /**
     * Standalone category column for the default (workspace-matched) match
     * drawer view (PRO-12048). Distinct from `ACCOUNT`, which conflates
     * Category/Cash ledger/Accrual ledger into a single basis-swapped column
     * for the legacy views (allocation drawer, audit log) that still use it.
     */
    CATEGORY = "category",
    /**
     * Standalone cash-ledger column for the default match drawer view.
     * Always populated (every business entity has a cash ledger), shown
     * alongside `ACCRUAL_LEDGER` for dual-ledger workspaces.
     */
    CASH_LEDGER = "cashLedger",
    /**
     * Standalone accrual-ledger column for the default match drawer view.
     * Only offered when the item's entity has an accrual ledger.
     */
    ACCRUAL_LEDGER = "accrualLedger",
    CLASS = "class",
    /**
     * The row's own revenue schedule (transactions and invoices only), with
     * Apply / Change / Remove that commit immediately. Offered when the
     * `schedules` flag is on.
     */
    SCHEDULE = "schedule",
    FINANCIAL_ACCOUNT = "financialAccount",
    AMOUNT = "amount",
    OPEN = "open",
    PAYMENT = "payment",
    ENTITY = "entity",
    ACTION = "action",
    REMOVE = "remove",
    LEFT_SPACER = "leftSpacer",
    RIGHT_SPACER = "rightSpacer",
}

export interface AccrualMatchTableCallbacks {
    onAction?: (item: MatchableItem) => void;
    onPaymentChange?: (lineItemId: string, value: number, max: number) => void;
    onRemainingAccountChange?: (id: string, accountId: string | null) => void;
    onRemainingCashAccountChange?: (
        id: string,
        accountId: string | null,
    ) => void;
    onRemainingCategoryChange?: (id: string, categoryId: string | null) => void;
    onRemainingAmountChange?: (id: string, amount: number) => void;
    onRemainingDescriptionChange?: (id: string, description: string) => void;
    onRemoveRow?: (id: string) => void;
}

export type PaymentAmounts = Record<string, number>;

export interface AccrualMatchSearchFooterState {
    totalCount: number;
    isLoading: boolean;
    pageCount: number;
    page: number;
    onPageChange: (page: number) => void;
    shouldShowPagination: boolean;
}

export type AccrualMatchSection =
    | "source"
    | "selected"
    | "search"
    | "remaining";

export enum AccrualMatchSortValue {
    DATE_ASC = "DATE_ASC",
    DATE_DESC = "DATE_DESC",
    COUNTERPARTY_ASC = "COUNTERPARTY_ASC",
    COUNTERPARTY_DESC = "COUNTERPARTY_DESC",
    DESCRIPTION_ASC = "DESCRIPTION_ASC",
    DESCRIPTION_DESC = "DESCRIPTION_DESC",
    ACCOUNT_ASC = "ACCOUNT_ASC",
    ACCOUNT_DESC = "ACCOUNT_DESC",
    CLASS_ASC = "CLASS_ASC",
    CLASS_DESC = "CLASS_DESC",
    AMOUNT_ASC = "AMOUNT_ASC",
    AMOUNT_DESC = "AMOUNT_DESC",
}

export const DEFAULT_ACCRUAL_MATCH_SORT = AccrualMatchSortValue.DATE_DESC;
